import { isPossiblePhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js/max";
import { parametersJsonError, sanitizeToolName, validateToolName } from "@/utils/tool-name-utils";
import type { HeaderRow } from "@/utils/tool-options";

/**
 * What the tool dialogs check before they send anything: the `tool` function's
 * own rules (supabase/functions/tool/{custom,native}/validation.ts), so a save
 * the server would refuse never leaves the dialog, and the reason shows under
 * the field that needs changing instead of arriving as the server's words.
 */

/** A field of the tool dialogs that can be wrong. */
export type ToolField =
  | "toolName"
  | "description"
  | "endpointUrl"
  | "parameters"
  | "timeout"
  | "smsStaticMessage"
  | "twilioAccountSid"
  | "twilioAuthToken"
  | "twilioFromNumber"
  | "transferDestination"
  | "transferDynamicPrompt"
  /** The value of the edit dialog's header row at that index. */
  | `headerValue${number}`;

/** Each field's error; a field that is fine has none. */
export type ToolFormErrors = Partial<Record<ToolField, string>>;

/** bot.py declares these itself, so a custom tool of one of these names is a second declaration. */
const RESERVED_TOOL_NAMES = ["end_call", "send_sms", "transfer_call", "report_voicemail"];

const blank = (text: string) => !text.trim();

/** The column's CHECK: whole seconds, 5 to 300. */
function timeoutError(seconds: number): string | undefined {
  return Number.isInteger(seconds) && seconds >= 5 && seconds <= 300
    ? undefined
    : "Enter a whole number of seconds from 5 to 300.";
}

/**
 * The name as it is sent (sanitized) is what the server judges: at most 64
 * characters (Gemini's cap), and not a built-in's name — on a create, and on an
 * edit that renames, so a row already stored under such a name stays editable.
 */
function toolNameError(name: string, storedName?: string): string | undefined {
  const { error } = validateToolName(name);
  if (error) return error;
  const sent = sanitizeToolName(name);
  if (sent.length > 64) return "Tool name can be at most 64 characters.";
  if (sent !== storedName && RESERVED_TOOL_NAMES.includes(sent)) {
    return `"${sent}" is reserved for a built-in tool. Choose another name.`;
  }
  return undefined;
}

function parsesAsUrl(text: string): boolean {
  try {
    new URL(text);
    return true;
  } catch {
    return false;
  }
}

function endpointUrlError(url: string): string | undefined {
  const trimmed = url.trim();
  if (!trimmed) return "Enter the endpoint URL.";
  return /^https?:\/\//.test(trimmed) && parsesAsUrl(trimmed)
    ? undefined
    : "Enter a full URL starting with http:// or https://, e.g. https://api.example.com/endpoint.";
}

export function customToolErrors(tool: {
  toolName: string;
  /** On an edit, the name the row has now. */
  storedToolName?: string;
  endpointUrl: string;
  parametersJson: string;
  timeoutSeconds: number;
}): ToolFormErrors {
  return {
    toolName: toolNameError(tool.toolName, tool.storedToolName),
    endpointUrl: endpointUrlError(tool.endpointUrl),
    parameters: parametersJsonError(tool.parametersJson),
    timeout: timeoutError(tool.timeoutSeconds),
  };
}

/**
 * A saved header's value is never shown, and it stays under the name it was
 * saved with: a saved row renamed while its value is still empty has nothing to
 * keep, and the server would refuse it. A row whose name is cleared is not sent.
 */
export function savedHeaderErrors(rows: HeaderRow[]): ToolFormErrors {
  return Object.fromEntries(
    rows.flatMap((row, index) =>
      row.saved && row.key.trim() && row.key !== row.originalKey && !row.value
        ? [[`headerValue${index}`, "Type this header's value again: a saved value stays under the name it was saved with."]]
        : [],
    ),
  );
}

export function smsToolErrors(sms: {
  contentType: "prompt" | "static";
  staticMessage: string;
  accountSid: string;
  authToken: string;
  /** On an edit, whether the tool has credentials saved. They are never shown:
   * empty fields keep them, a token typed alone replaces only the token, and a
   * typed SID needs its token typed too. */
  credentialsSaved?: boolean;
  fromNumber: string;
  timeoutSeconds: number;
}): ToolFormErrors {
  const sid = sms.accountSid.trim();
  const token = sms.authToken.trim();
  const from = sms.fromNumber.trim();
  const fromIsValid =
    /^\+[1-9]\d{1,14}$/.test(from) || (/^[A-Za-z0-9 ]{1,11}$/.test(from) && /[A-Za-z]/.test(from));
  return {
    smsStaticMessage:
      sms.contentType === "static" && blank(sms.staticMessage) ? "Enter the message to send." : undefined,
    twilioAccountSid: !sid
      ? token && !sms.credentialsSaved ? "Enter the Account SID this Auth Token belongs to, or clear the token." : undefined
      : /^AC[0-9a-fA-F]{32}$/.test(sid)
        ? undefined
        : "A Twilio Account SID starts with AC followed by 32 hex characters.",
    twilioAuthToken: sid && !token ? "Enter the Auth Token for this Account SID, or clear the SID." : undefined,
    twilioFromNumber:
      from && !fromIsValid
        ? "Use E.164 format (e.g. +15551234567) or an alphanumeric sender ID (max 11 characters)."
        : undefined,
    timeout: timeoutError(sms.timeoutSeconds),
  };
}

export function transferToolErrors(transfer: {
  mode: "static" | "dynamic";
  destination: string;
  dynamicPrompt: string;
  description: string;
  /** The edit dialog's rule. A create may leave it blank: the server writes its default. */
  descriptionRequired?: boolean;
}): ToolFormErrors {
  if (transfer.mode === "dynamic") {
    return {
      transferDynamicPrompt: blank(transfer.dynamicPrompt)
        ? "Explain how the agent should choose the number to transfer to."
        : undefined,
    };
  }
  const destination = transfer.destination.trim();
  return {
    description:
      transfer.descriptionRequired && blank(transfer.description)
        ? "Describe when the agent should transfer calls."
        : undefined,
    // bot.py dials it as it is: a static transfer without a whole number fails every call it is used on.
    // A country code alone is what an erased number used to be saved as: the box shows it empty.
    transferDestination: !destination || /^\+\d{1,3}$/.test(destination)
      ? "Enter the phone number to transfer to."
      : isPossiblePhoneNumber(destination)
        ? undefined
        : "Enter a complete phone number for the selected country.",
  };
}

/**
 * A destination as bot.py should dial it. The possible-number check reads past
 * a trunk prefix typed after the country code (+1 1…, +44 0…, +91 0…), but the
 * raw text would be dialled with it; what cannot be read is left as it is.
 */
export function dialableNumber(destination: string): string {
  return parsePhoneNumberFromString(destination.trim())?.number ?? destination.trim();
}
